import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersResponses";
import type { ClientBuilderRole } from "api-types/permissions/permissionsResponses";
import type { PlanSetTemplate } from "building-types/views/planSets";
import type { Table } from "common/data/table";

export interface PlanDocumentPermissionCheckParams {
  hasPermission: (permission: ClientBuilderUserPermission) => boolean;
  isSuperAdmin: boolean;
  userId?: string;
  homePlanSalesAgentId?: string | null;
  locationSalesAgentId?: string | null;
  planSetTemplates: Table<PlanSetTemplate>;
  /** The roles assigned to the current user (not all available roles) */
  userRoles?: ClientBuilderRole[];
}

type BuiltInPlanSetPermissionCheckParams = Pick<
  PlanDocumentPermissionCheckParams,
  "hasPermission" | "userId" | "homePlanSalesAgentId" | "locationSalesAgentId"
>;

/**
 * Checks if a user has export permissions for a given plan set template based on their role.
 * Used for filtering which templates a user can see/download.
 */
export function hasExportPermissionForTemplate(
  template: PlanSetTemplate,
  userRoles: ClientBuilderRole[] | undefined,
  isAssignedSalesAgent: boolean,
): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  const exportPerms = template.exportPermissions ?? {};

  return userRoles.some((role) => {
    const permission = exportPerms[role.name];

    if (permission === "ALLOW") {
      return true;
    }

    if (permission === "IF_ASSIGNED") {
      return isAssignedSalesAgent;
    }

    return false;
  });
}

/**
 * Determines if a user can download built-in plan sets.
 *
 * Rules:
 * - Users with DOWNLOAD_BUILT_IN_PLAN_SETS permission can always download
 * - Sales agents can download if they are the assigned sales agent
 */
export function canDownloadBuiltInPlanSets({
  hasPermission,
  userId,
  homePlanSalesAgentId,
  locationSalesAgentId,
}: BuiltInPlanSetPermissionCheckParams): boolean {
  if (hasPermission("DOWNLOAD_BUILT_IN_PLAN_SETS")) {
    return true;
  }

  // Sales agents can download if they are the assigned sales agent
  if (!hasPermission("DOWNLOAD_PLAN_SETS_IF_SALES_STAFF")) {
    return false;
  }

  const isAssignedSalesAgent = userId && (userId === homePlanSalesAgentId || userId === locationSalesAgentId);
  return !!isAssignedSalesAgent;
}

/**
 * Determines if a user can download lot-specific (user-defined) plan sets.
 *
 * Rules:
 * - Super admins can always download
 * - Users with DOWNLOAD_PLAN_SETS permission can always download
 * - Users can download if they have role-based export permissions:
 *   - ALLOW: always allowed
 *   - IF_ASSIGNED: allowed if user is the assigned sales agent
 *   - DENY: not allowed
 */
export function canDownloadLotSpecificPlanSets({
  hasPermission,
  userId,
  homePlanSalesAgentId,
  locationSalesAgentId,
  planSetTemplates,
  userRoles,
}: PlanDocumentPermissionCheckParams): boolean {
  if (hasPermission("DOWNLOAD_PLAN_SETS")) {
    return true;
  }

  // Check if user is the assigned sales agent
  const isAssignedSalesAgent = !!(userId && (userId === homePlanSalesAgentId || userId === locationSalesAgentId));

  // Check if any template grants permission to this user
  for (const template of Object.values(planSetTemplates)) {
    if (hasExportPermissionForTemplate(template, userRoles, isAssignedSalesAgent)) {
      return true;
    }
  }

  return false;
}

/**
 * Determines if a user can download some plan sets (built-in or lot-specific).
 */
export function canDownloadSomePlanSets(params: PlanDocumentPermissionCheckParams): boolean {
  return canDownloadBuiltInPlanSets(params) || canDownloadLotSpecificPlanSets(params);
}
